import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import purchasesService from '../services/purchasesService';
import { useNotification } from '../context/NotificationContext';
import '../styles/general.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(value) || 0
  );

/**
 * PurchaseOrderDetailPage
 * Read-only view of a purchase order. Unlike the edit form it renders
 * cancelled orders too, so a cancelled record stays inspectable after it drops
 * out of the default list.
 */
const PurchaseOrderDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = useCallback(async () => {
    try {
      const res = await purchasesService.getPurchaseOrderById(id);
      setOrder(res.data);
    } catch (err) {
      console.error('Error loading purchase order:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!order) {
    return (
      <div className="page-container-wide">
        <div className="empty-state">
          <p>{t('purchases.noPurchaseOrders')}</p>
        </div>
        <button onClick={() => navigate('/purchases')} className="btn btn-secondary">
          {t('purchases.backToList')}
        </button>
      </div>
    );
  }

  const anyInventoried = (order.items || []).some((item) => item.is_inventoried);

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('purchases.detailTitle')} — {order.order_number}</h1>
        <span className="tag-badge">
          {order.is_active ? t('common.active') : t('purchases.cancelledBadge')}
        </span>
      </div>

      <fieldset className="form-fieldset">
        <legend>{t('purchases.generalData')}</legend>

        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('table.supplier')}</div>
            <div className="section-value">{order.supplier_name}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.supplierOrderNumber')}</div>
            <div className="section-value">{order.supplier_order_number || '—'}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.invoiceReference')}</div>
            <div className="section-value">{order.invoice_reference || '—'}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.effectiveDate')}</div>
            <div className="section-value">{order.effective_date}</div>
          </div>
        </div>

        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('purchases.category')}</div>
            <div className="section-value">{order.category_name}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.subcategory')}</div>
            <div className="section-value">{order.subcategory_name}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.frequency')}</div>
            <div className="section-value">
              {order.frequency ? t(`purchases.frequencyOptions.${order.frequency}`) : '—'}
            </div>
          </div>
        </div>

        <div className="grid-3">
          <div className="section">
            <div className="section-header">{t('purchases.isPaid')}</div>
            <div className="section-value">
              {order.is_paid ? t('common.yes') : t('common.no')}
            </div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.isInvoiced')}</div>
            <div className="section-value">
              {order.is_invoiced ? t('common.yes') : t('common.no')}
            </div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.isCredit')}</div>
            <div className="section-value">
              {order.is_credit ? t('common.yes') : t('common.no')}
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('purchases.itemsSection')}</legend>

        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('table.itemNumber')}</th>
                <th>{t('table.description')}</th>
                <th>{t('table.measure')}</th>
                <th>{t('table.quantity')}</th>
                <th>{t('table.unitPrice')}</th>
                <th>{t('purchases.isInventoried')}</th>
                <th>{t('purchases.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, index) => (
                <tr key={item.id ?? index}>
                  <td className="text-center">{index + 1}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{item.measure_name || '—'}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-right">{formatCurrency(item.unit_price)}</td>
                  <td className="text-center">
                    {item.is_inventoried ? t('common.yes') : t('common.no')}
                  </td>
                  <td className="text-right text-bold">{formatCurrency(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {anyInventoried && (
          <p className="text-muted mt-10">{t('purchases.inventoriedNote')}</p>
        )}
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('purchases.totalsSection')}</legend>

        <div className="grid-2">
          <div className="section">
            <div className="section-header">{t('purchases.subtotal')}</div>
            <div className="section-value" data-testid="detail-subtotal">
              {formatCurrency(order.subtotal)}
            </div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.taxes')}</div>
            <div className="section-value">{formatCurrency(order.taxes)}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.taxes2')}</div>
            <div className="section-value">{formatCurrency(order.taxes2)}</div>
          </div>
          <div className="section">
            <div className="section-header">{t('purchases.grandTotal')}</div>
            <div className="total-emphasis" data-testid="detail-grand-total">
              {formatCurrency(order.total)}
            </div>
          </div>
        </div>
      </fieldset>

      {!order.is_active && (
        <p className="alert alert-warning">{t('purchases.cancelledReadOnly')}</p>
      )}

      <div className="form-actions">
        {order.is_active && (
          <button
            onClick={() => navigate(`/purchases/${order.id}/edit`)}
            className="btn btn-primary"
          >
            {t('common.edit')}
          </button>
        )}
        <button onClick={() => navigate('/purchases')} className="btn btn-secondary">
          {t('purchases.backToList')}
        </button>
      </div>
    </div>
  );
};

export default PurchaseOrderDetailPage;
